import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks/useAppStore';
import { showSnackbar } from '../../store/snackbarSlice';
import { getPusher } from '../../lib/pusher';
import { crmPhylaxApi } from '../../store/api/crmPhylaxApi';

/**
 * Escucha en private-user-{userId} los eventos del Agente Virtual (Phylax) y
 * muestra snackbars: escalaciones, créditos bajos y visitas. El backend ya rutea
 * cada evento solo a los usuarios que corresponden (por rol), así que el hook se
 * monta para cualquier sesión CRM.
 *
 * Importante: NO hace pusher.unsubscribe en el cleanup — el canal private-user es
 * compartido con otros hooks (ej. useSupportBugAlerts); solo desbindea sus eventos.
 * Llamarlo en un punto montado siempre durante la sesión CRM (DashboardLayout).
 */
export function usePhylaxNotifications() {
  const accessToken   = useAppSelector((s) => s.auth.accessToken);
  const dispatch      = useDispatch();
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!accessToken || subscribedRef.current) return;

    let userId: string;
    try {
      userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string;
    } catch {
      return;
    }

    const pusher  = getPusher(accessToken);
    const channel = pusher.subscribe(`private-user-${userId}`);

    const onEscalation = (data: { customer_name?: string; phone?: string; priority?: string }) => {
      dispatch(showSnackbar({
        message: `🤖 El Agente Virtual derivó una conversación${data.customer_name ? ` de ${data.customer_name}` : ''}${data.phone ? ` (${data.phone})` : ''}`,
        severity: data.priority === 'urgent' ? 'warning' : 'info',
      }));
      dispatch(crmPhylaxApi.util.invalidateTags(['PhylaxEscalation', 'PhylaxConversation', 'PhylaxAnalytics']));
    };

    const onEscalationUpdate = () => {
      dispatch(crmPhylaxApi.util.invalidateTags(['PhylaxEscalation', 'PhylaxConversation', 'PhylaxAnalytics']));
    };

    const onCreditLow = (data: { balance?: number }) => {
      dispatch(showSnackbar({
        message: `⚠️ Créditos del Agente Virtual bajos (saldo: ${data.balance ?? '—'}). Considerá recargar.`,
        severity: 'warning',
      }));
    };

    const onVisit = (data: { type?: string; visit?: { date?: string; time?: string } }) => {
      const isNew = data.type === 'visit.scheduled';
      const when  = data.visit?.date ? ` — ${data.visit.date}${data.visit.time ? ` ${data.visit.time}` : ''}` : '';
      dispatch(showSnackbar({
        message: `📅 ${isNew ? 'Nueva visita agendada' : 'Visita actualizada'} por el Agente Virtual${when}`,
        severity: 'info',
      }));
      dispatch(crmPhylaxApi.util.invalidateTags(['PhylaxEvent', 'PhylaxAnalytics']));
    };

    channel.bind('phylax.escalation', onEscalation);
    channel.bind('phylax.escalation.update', onEscalationUpdate);
    channel.bind('phylax.credit.low', onCreditLow);
    channel.bind('phylax.visit', onVisit);
    subscribedRef.current = true;

    return () => {
      channel.unbind('phylax.escalation', onEscalation);
      channel.unbind('phylax.escalation.update', onEscalationUpdate);
      channel.unbind('phylax.credit.low', onCreditLow);
      channel.unbind('phylax.visit', onVisit);
      subscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, dispatch]);
}
